import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

export interface DashboardRow {
  readonly portalDashboardId: string;
  readonly dashboardCode: string;
  readonly title: string;
  readonly dashboardType: string;
  readonly isDefault: boolean;
  readonly status: string;
}

const DASHBOARD_TYPES = ['PERSONAL', 'VENDOR', 'AGENCY', 'CUSTOMER', 'PERFORMANCE', 'APPROVAL', 'COMPLIANCE'];
const PAGE = 25;

/**
 * Shared responsive view (§17 / §19) behind the CustomerPortalDashboard,
 * VendorPortalDashboard and AgencyPortalDashboard screens (§27). Single source
 * of truth (Rule-8: no duplicated screen logic) — each stakeholder screen fixes
 * the `segment` label + the default dashboard type it provisions. Viewing needs
 * M42_DASHBOARD_VIEW, provisioning needs M42_DASHBOARD_MANAGE.
 */
export function PortalDashboardView({
  segment,
  defaultType,
}: {
  readonly segment: string;
  readonly defaultType: string;
}): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<DashboardRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [title, setTitle] = useState('');
  const [dashboardType, setDashboardType] = useState(defaultType);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: DashboardRow[]; total: number }>(`/v1/portal/dashboards?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, page, reload]);

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!title.trim()) { setActionError(t('m42.dashboardView.titleRequired')); return; }
    try {
      await apiRequest('/v1/portal/dashboards', {
        method: 'POST',
        body: { title: title.trim(), dashboardType, portalUserId: null },
        token,
      });
      setNotice(t('m42.dashboardView.provisioned'));
      setTitle('');
      setPage(0);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m42.dashboardView.notAuthProvision'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label={`portal-dashboard-${segment}`}>
      <h1>{t(`m42.dashboardView.title.${segment}`)}</h1>

      <section aria-label="dashboard-provision">
        <h2>{t('m42.dashboardView.provisionHeading')}</h2>
        <form aria-label="provision-form" onSubmit={(e) => void create(e)}>
          <label>{t('m42.common.title')}<input aria-label="field-dashboard-title" value={title} onChange={(e) => setTitle(e.target.value)} /></label>
          <label>{t('m42.common.type')}
            <select aria-label="field-dashboard-type" value={dashboardType} onChange={(e) => setDashboardType(e.target.value)}>
              {DASHBOARD_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-provision">{t('m42.dashboardView.provisionBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="dashboard-action-error">{actionError}</p>}
        {notice && <p aria-label="dashboard-notice">{notice}</p>}
      </section>

      {error && <p role="alert" aria-label="dashboard-error">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="dashboard-total">{t('m42.dashboardView.total', { count: total })}</p>
          <DataTable
            ariaLabel="dashboard-table"
            rows={rows}
            rowKey={(d) => d.portalDashboardId}
            emptyMessage={t('m42.dashboardView.empty')}
            columns={[
              { key: 'code', header: t('m42.common.code'), cellAriaLabel: (d) => `dashboard-code-${d.portalDashboardId}`, render: (d) => d.dashboardCode },
              { key: 'title', header: t('m42.common.title'), render: (d) => d.title },
              { key: 'type', header: t('m42.common.type'), render: (d) => d.dashboardType },
              { key: 'status', header: t('m42.common.status'), render: (d) => d.status },
            ]}
          />

          <nav aria-label="dashboard-pagination">
            <Button variant="secondary" type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('m42.common.previous')}</Button>
            <span aria-label="page-indicator">{t('m42.common.pageOf', { page: page + 1, pages: Math.max(1, Math.ceil(total / PAGE)) })}</span>
            <Button variant="secondary" type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>{t('m42.common.next')}</Button>
          </nav>
        </>
      )}
    </main>
  );
}
